import React, { useCallback, useMemo, useState } from 'react';
import { TabSection, Chip, IconDiv } from './style';
import SVGIcon from '../../assets/images/svg/SVGIcon';
import { Dropdown, DropdownDivider, DropdownItem } from '../Dropdown';
import { rootStore } from '../../mobx/rootStore';
import { dueDateItems, filterOptions, menuItems } from '../../helpers/list';
import { PRIORITY_ICON_NAMES, STATUS_TYPE_LABELS, statusItems } from '../../global/constants';
import { VIEW_FILTER_LABELS, ViewFilters, VIEWS_FILTER_TYPE } from '../../mobx/interfaces/view';
import { observer } from 'mobx-react-lite';
import Avatar from '../avatar/Avatar';
import { renderStatusIconName } from '../../helpers/common';
import AssigneeDropdownContent from '../dropdowns/assignPopup/AssigneeDropdownContent';
import { UserModel } from '../../mobx/models/user';
import DropdownSearch from '../Dropdown/DropdownSearch';

interface FilterChipsProps {
  onChangePriority: (type: number) => void;
  onChangeStatus: (type: number) => void;
  onChangeProject: (id: string) => void;
  onChangeDueDate: (date: string) => void;
  onChangeAssign: (user: UserModel) => void;
}

const FilterChips: React.FC<FilterChipsProps> = (props: FilterChipsProps) => {
  const { onChangePriority, onChangeStatus, onChangeProject, onChangeDueDate, onChangeAssign } = props;
  //Mobx store variables
  const { viewStore, projectStore, workspaceStore } = rootStore;
  const { createViewInput } = viewStore;
  const { projects } = projectStore;
  const { currentWorkspace } = workspaceStore;

  //State variables
  const [projectList, setProjectList] = useState(projects);
  const [userList, setUserList] = useState(currentWorkspace?.users);

  const dueDateOperatorOptions = [
    {
      label: 'after',
      value: VIEWS_FILTER_TYPE.AFTER
    },
    {
      label: 'before',
      value: VIEWS_FILTER_TYPE.BEFORE
    }
  ];

  const operatorOptions = (isMany: boolean, isDueDate: boolean) => {
    if (isDueDate) {
      return dueDateOperatorOptions;
    }
    return [
      {
        label: isMany ? 'is any of' : 'is',
        value: VIEWS_FILTER_TYPE.IS
      },
      {
        label: 'is not',
        value: VIEWS_FILTER_TYPE.IS_NOT
      }
    ];
  };

  const getFilterIcon = (filterName: string) => {
    return filterOptions[filterName as keyof typeof filterOptions];
  };

  const onChangeProjectSearch = useCallback(
    (value: string) => {
      let updatedList = projects;
      if (value) {
        const searchNewText = value.trim().toLowerCase();
        const words = searchNewText.split(' ').filter((word) => word.length > 0);
        updatedList = projects?.filter((item) => {
          const title = item?.name.trim().toLowerCase() || '';
          return words.some((word) => title.includes(word));
        });
      }
      setProjectList(updatedList);
    },
    [projects]
  );

  const onChangeAssignSearch = useCallback(
    (value: string) => {
      let updatedList = currentWorkspace?.users;
      if (value) {
        const searchNewText = value.trim().toLowerCase();
        const words = searchNewText.split(' ').filter((word) => word.length > 0);
        updatedList = currentWorkspace?.users?.filter((item) => {
          const title = item?.name.trim().toLowerCase() || '';
          return words.some((word) => title.includes(word));
        });
      }
      setUserList(updatedList);
    },
    [currentWorkspace?.users]
  );

  const getFilterDropdownContent = useCallback(
    (filterName: string, filterValue: any) => {
      const data = Object.values(filterValue)[0] as string[] | number[];

      switch (filterName) {
        case 'priority':
          return {
            type: filterName,
            label: data?.length === 1 ? PRIORITY_ICON_NAMES[data[0] as number] : `${data?.length} priorities`,
            icon: 'Priority-High-icon'
          };
        case 'dueDate':
          return {
            type: filterName,
            label: data[0] as string //Need changes
            // icon: 'calendar-icon'
          };
        case 'status':
          return {
            type: filterName,
            label: data?.length === 1 ? STATUS_TYPE_LABELS[data[0] as number] : `${data?.length} statuses`,
            icon: 'status-icon'
          };
        case 'project':
          return {
            type: filterName,
            label:
              data?.length === 1
                ? projects?.find((project) => project.id === data[0])?.name
                : `${data?.length} projects`,
            icon: 'navbar-project-icon'
          };
        case 'assign':
          return {
            type: filterName,
            label:
              data?.length === 1
                ? currentWorkspace?.users?.find((user) => user.id === data[0])?.name
                : `${data?.length} assignees`,
            icon: 'user-icon'
          };
        default:
          return;
      }
    },
    [currentWorkspace?.users, projects]
  );

  const getFilterDropdown = useCallback(
    (filterName: string) => {
      switch (filterName) {
        case 'priority':
          return menuItems.map((item, index) => {
            const isChecked = createViewInput?.filters?.priority?.priorities.includes(item.key);
            return (
              <DropdownItem
                key={index}
                label={item.label}
                onClick={() => onChangePriority(item.key)}
                iconName={item.icon}
                isCheckBox={true}
                isChecked={isChecked}
              />
            );
          });
        case 'status':
          return statusItems.map((item, index) => {
            const isChecked = createViewInput?.filters?.status?.statuses.includes(item.type);
            return (
              <DropdownItem
                key={index}
                label={item.label}
                onClick={() => onChangeStatus(item.type)}
                iconName={item.iconName}
                isCheckBox={true}
                isChecked={isChecked}
              />
            );
          });
        case 'project':
          return (
            <>
              <DropdownSearch onChangeSearch={onChangeProjectSearch} />
              <DropdownDivider />
              {projectList?.map((item, index) => {
                const isChecked = createViewInput?.filters?.project?.projects.includes(item.id);
                return (
                  <DropdownItem
                    key={index}
                    label={item.name}
                    onClick={() => onChangeProject(item.id)}
                    isCheckBox={true}
                    isChecked={isChecked}
                  />
                );
              })}
            </>
          );
        case 'dueDate':
          return dueDateItems.map((item, index) => {
            return <DropdownItem key={index} label={item.label} onClick={() => onChangeDueDate(item.label)} />;
          });
        case 'assign':
          return (
            <>
              <DropdownSearch onChangeSearch={onChangeAssignSearch} />
              <DropdownDivider />
              <AssigneeDropdownContent
                data={userList || []}
                isMultiAssignee={true}
                isView={true}
                isCreateTaskModal={false}
                createViewInput={createViewInput}
                onClickUnassignUser={() => {
                  createViewInput.setAssignFilter([], createViewInput?.filters?.assign?.type);
                }}
                onClickAssignUser={onChangeAssign}
              />
            </>
          );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      createViewInput?.filters?.assign?.assignees,
      createViewInput?.filters?.priority?.priorities,
      createViewInput?.filters?.project?.projects,
      createViewInput?.filters?.status?.statuses,
      createViewInput?.filters?.dueDate?.date,
      currentWorkspace?.users,
      userList,
      onChangeDueDate,
      onChangePriority,
      onChangeProject,
      onChangeStatus,
      projectList,
      projects
    ]
  );

  const tabSectionContent = useMemo(() => {
    return Object.entries(createViewInput?.filters || {}).map(([filterName, filterValue]) => {
      const filterIcon = getFilterIcon(filterName);
      const filterDropdownContent = getFilterDropdownContent(filterName, filterValue);
      const filterDropdown = getFilterDropdown(filterName);
      const filterType = filterValue.type;
      return {
        filterName,
        filterIcon,
        filterValue,
        filterDropdownContent,
        filterDropdown,
        filterType
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    createViewInput?.filters,
    createViewInput?.filters?.status?.type,
    createViewInput?.filters?.priority?.type,
    createViewInput?.filters?.assign?.type,
    createViewInput?.filters?.project?.type,
    createViewInput?.filters?.dueDate?.type,
    getFilterDropdown,
    getFilterDropdownContent
  ]);

  const selectedFilter = useCallback(
    (filterName: string, value: any, type: number) => {
      switch (filterName) {
        case 'status':
          return createViewInput.setStatusFilter(value as number[], type);
        case 'priority':
          return createViewInput.setPriorityFilter(value as number[], type);
        case 'assign':
          return createViewInput.setAssignFilter(value as string[], type);
        case 'project':
          return createViewInput.setProjectFilter(value as string[], type);
        case 'dueDate':
          return createViewInput.setDueDateFilter(value as string[], type);
      }
    },
    [createViewInput]
  );

  const onChangeOperator = useCallback(
    (filterName: string, operator: number) => {
      const filterValue = createViewInput?.filters?.[filterName as keyof ViewFilters];
      const key = Object.keys(filterValue)[0];
      const value = filterValue[key as keyof typeof filterValue];
      selectedFilter(filterName, value, operator);
    },
    [createViewInput?.filters, selectedFilter]
  );

  const onRemoveFilter = useCallback(
    (filterName: string) => {
      selectedFilter(filterName, [], filterName === 'dueDate' ? VIEWS_FILTER_TYPE.AFTER : VIEWS_FILTER_TYPE.IS);
    },
    [selectedFilter]
  );

  return (
    <>
      {tabSectionContent?.map((item) => {
        const { filterName, filterIcon, filterValue, filterDropdownContent, filterDropdown } = item;
        const data = Object.values(filterValue)[0] as string[] | number[];
        if (data?.length === 0) return null;
        return (
          <TabSection>
            <Chip>
              <IconDiv>
                <SVGIcon
                  name={filterIcon.iconName}
                  stroke={filterName === 'project' ? 'var(--text-primary)' : ''}
                  width='16px'
                  height='16px'
                  viewBox={filterIcon.viewBox}
                  className='task-human-icon'
                />
              </IconDiv>
              <span>{filterIcon.label}</span>
            </Chip>
            <Dropdown
              content={
                <Chip>
                  <span>
                    {data?.length > 1 && filterValue.type === VIEWS_FILTER_TYPE.IS
                      ? 'is any of'
                      : VIEW_FILTER_LABELS[filterValue.type]}
                  </span>
                </Chip>
              }
              trigger='click'>
              {operatorOptions(
                data?.length > 1,
                filterName === 'dueDate' &&
                  filterDropdownContent?.label !== 'In the past' &&
                  filterDropdownContent?.label !== 'No due date'
              ).map((option) => (
                <DropdownItem
                  key={option.value}
                  label={option.label}
                  onClick={() => onChangeOperator(filterName, option.value)}
                />
              ))}
            </Dropdown>
            <Dropdown
              isMinWidth={138}
              content={
                <Chip>
                  {data?.length === 1 &&
                    filterName !== 'status' &&
                    filterName !== 'assign' &&
                    filterDropdownContent?.icon && (
                      <IconDiv key={filterDropdownContent?.label} style={{ marginLeft: '0' }}>
                        <SVGIcon
                          name={filterDropdownContent?.icon}
                          width='16px'
                          height='16px'
                          viewBox={filterName === 'dueDate' ? '0 0 18 18' : '0 0 16 16'}
                          stroke={filterName === 'status' ? 'var(--text-primary)' : ''}
                        />
                      </IconDiv>
                    )}
                  {(filterName === 'assign' || filterName === 'status') &&
                    data?.map((item, index) => {
                      const user =
                        filterName === 'assign' ? currentWorkspace?.users?.find((user) => user.id === item) : null;
                      if (index > 1) return null;
                      return (
                        <IconDiv key={index} style={{ marginLeft: index > 0 ? '-12px' : '0' }}>
                          {filterName === 'assign' ? (
                            <Avatar imgSrc={user?.profile_photo || ''} name={user?.name || ''} size={14} />
                          ) : (
                            <SVGIcon
                              name={renderStatusIconName(item as number)}
                              width='16px'
                              height='16px'
                              viewBox='0 0 16 16'
                            />
                          )}
                        </IconDiv>
                      );
                    })}
                  <span>{filterDropdownContent?.label}</span>
                </Chip>
              }
              trigger='click'>
              {filterDropdown}
            </Dropdown>
            <Chip onClick={() => onRemoveFilter(filterName)}>
              <IconDiv>
                <SVGIcon
                  name='cross-icon'
                  width='10px'
                  height='10px'
                  viewBox='0 0 10 10'
                  stroke='var(--text-secondary)'
                />
              </IconDiv>
            </Chip>
          </TabSection>
        );
      })}
    </>
  );
};
export default observer(FilterChips);
